import axios from 'axios';
import {
  Transaction,
  Ticket,
  TicketType,
  AuthResponse,
  TransactionInitiationResponse,
  TicketValidationResponse,
  TransactionInitiationApiResponse,
  AdminLoginApiResponse,
  TicketValidationApiResponse,
  TicketUseApiResponse,
  TicketTypesApiResponse,
  TransactionStatusApiResponse,
  TransactionTicketApiResponse,
  ValidationError
} from '../types';
import { getKioskId } from '../utils/kioskId';

// Use environment variable if set, otherwise detect from current hostname
const getApiBaseUrl = (): string => {
  // If explicitly set in environment, use that
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Otherwise, dynamically construct based on current location
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const backendPort = process.env.REACT_APP_API_PORT || '80';

  return `${protocol}//${hostname}:${backendPort}/api`;
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('authenticated');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const ticketTypeService = {
  getTicketTypes: async (): Promise<TicketType[]> => {
    const response = await api.get<TicketTypesApiResponse>('/ticket-types');
    return response.data.data;
  },

  // Admin CRUD operations

  create: async (ticketType: Partial<TicketType>): Promise<TicketType> => {
    const response = await api.post('/admin/ticket-types', ticketType);
    return response.data.data;
  },

  update: async (id: string, ticketType: Partial<TicketType>): Promise<TicketType> => {
    const response = await api.put(`/admin/ticket-types/${id}`, ticketType);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/ticket-types/${id}`);
  },
};

export const adminAuthService = {
  login: async (username: string, password: string): Promise<AuthResponse> => {
    const response = await api.post<AdminLoginApiResponse>('/agent/login', {
      username,
      password,
    });
    return {
      success: true,
      data: {
        user: response.data.user,
        token: response.data.token,
        token_type: response.data.token_type || 'Bearer',
      },
    };
  },

  logout: async () => {
    const token = localStorage.getItem('token');
    const response = await api.post('/agent/logout', {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },
};

export const transactionService = {
  initiate: async (ticketType: TicketType): Promise<TransactionInitiationResponse> => {
    const kioskId = getKioskId();
    const response = await api.post<TransactionInitiationApiResponse>('/transaction/initiate', {
      ticket: {
        type: ticketType.id,
        price: ticketType.price,
      },
      kiosk_id: kioskId,
    });
    return {
      transaction_id: response.data.data.transaction_id,
      status: response.data.data.status,
      amount: response.data.data.amount,
      payment_method: response.data.data.payment_method,
      ticket: response.data.data.ticket!,
      payment_url: response.data.payment_url,
    };
  },

  getStatus: async (transactionId: string): Promise<Transaction> => {
    const response = await api.get<TransactionStatusApiResponse>(`/transaction/${transactionId}/status`);
    return response.data.data;
  },

  getTicket: async (transactionId: string): Promise<Ticket> => {
    const response = await api.get<TransactionTicketApiResponse>(`/transaction/${transactionId}/ticket`);
    return response.data.data;
  },

  getAll: async (page: number = 1, limit: number = 10, status?: string, search?: string): Promise<{data: Transaction[], total: number}> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status && status !== 'all') {
      params.append('status', status);
    }
    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    const response = await api.get(`/transaction?${params.toString()}`);
    return {
      data: response.data.data,
      total: response.data.total || response.data.data.length
    };
  },
};

export const ticketService = {
  validate: async (qrCode: string): Promise<TicketValidationResponse> => {
    try {
      const response = await api.post<TicketValidationApiResponse>('/ticket/validate', {
        qr_code: qrCode,
      });
      return {
        ticket: response.data.data,
        status: response.data.data.status ?? null,
        transaction: null,
        valid: true,
        message: null,
      };
    } catch (error: any) {
      if (error.response && error.response.status === 400) {
        const validationError = error.response.data as ValidationError;
        return {
          ticket: null,
          status: null,
          transaction: null,
          valid: false,
          message: validationError.message,
        };
      }
      throw error;
    }
  },

  use: async (ticketId: string): Promise<Ticket> => {
    const response = await api.post<TicketUseApiResponse>(`/ticket/${ticketId}/use`);
    return response.data.data;
  },

  getAll: async (page: number = 1, limit: number = 10, status?: string, search?: string): Promise<{data: Ticket[], total: number}> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status && status !== 'all') {
      params.append('status', status);
    }
    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    const response = await api.get(`/ticket?${params.toString()}`);
    return {
      data: response.data.data,
      total: response.data.total || response.data.data.length
    };
  },
};

export default api;
